import { ViewConfig } from '../../../models/view-config.interface';
import { DeliveryNotesPaginatedService } from '@app/consignor/services/delivery-notes-paginated.service';
import { AppStore } from '@app/stores/appStore';
import { ConsignorViewActionsService } from '../consignor-view-actions.service';
import { TableActionsConfig } from '@app/shared/components/table-cell-components/table-actions.component';
import { ColumnCreator } from '../column-creator';
import {
    TableRowData,
    DeliveryNoteData,
    hasDeliveryNoteBundleStatus,
    hasReadOnly,
    isDeliveryNoteData,
    hasDateShipped,
    hasDeliveryNoteBundleNumber,
} from '@app/shared/models/table-data-types';
import { SelectionStore } from '@app/stores/selectionStore';

export interface ConsignorDeliveryNotesConfigDeps {
    deliveryNotesPaginatedService: DeliveryNotesPaginatedService;
    actionsService: ConsignorViewActionsService;
    appStore: InstanceType<typeof AppStore>;
    selectionStore: InstanceType<typeof SelectionStore>;
}

export class ConsignorDeliveryNotesConfig {
    static create(deps: ConsignorDeliveryNotesConfigDeps): ViewConfig {
        return {
            id: 'consignor-deliverynotes',
            name: 'Delivery Notes',
            translationKey: 'OUTGOING-GOODS.TOGGLER.DELIVERY-NOTES',
            type: 'deliverynotes',
            userRole: 'consignor',
            apiConfig: {
                loadData: () => {
                    const orgSiteKey =
                        deps.appStore.currentOrganizationSiteKey();
                    if (!orgSiteKey) {
                        return Promise.resolve([]);
                    }
                    return deps.deliveryNotesPaginatedService.loadDeliveryNotesPaginated(
                        orgSiteKey
                    );
                },
            },
            tableConfig: {
                columns: [
                    ColumnCreator.select({
                        selectableCondition: (item: TableRowData) => {
                            const isNotInBundle = !(item as any)
                                .deliveryNoteBundleNumber;
                            const isNotReadOnly =
                                !hasReadOnly(item) || !item.read_only;
                            return isNotInBundle && isNotReadOnly;
                        },
                    }),
                    ColumnCreator.actions({
                        width: '150px',
                        configuration: {
                            type: 'deliveryNote',
                            showView: true,
                            showDownload: true,
                            showEdit: true,
                            showDelete: true,
                            showDeletePermanent: true,

                            // Endpoints
                            viewEndpoint: (item: TableRowData) => {
                                if (isDeliveryNoteData(item)) {
                                    return deps.actionsService.viewDeliveryNote(
                                        item as DeliveryNoteData
                                    );
                                }
                                return undefined;
                            },
                            downloadEndpoint: (item: TableRowData) => {
                                if (isDeliveryNoteData(item)) {
                                    return deps.actionsService.downloadDeliveryNote(
                                        item as DeliveryNoteData
                                    );
                                }
                                return undefined;
                            },
                            editEndpoint: (item: TableRowData) => {
                                if (isDeliveryNoteData(item)) {
                                    return deps.actionsService.editDeliveryNote(
                                        item as DeliveryNoteData
                                    );
                                }
                                return undefined;
                            },
                            deleteEndpoint: (
                                item: TableRowData,
                                bundleKey?: string
                            ) => {
                                if (isDeliveryNoteData(item)) {
                                    // For delivery notes, we remove them from their current bundle
                                    const deliveryNoteBundleKey =
                                        item.deliveryNoteBundleKey || '';
                                    return deps.actionsService.deleteDeliveryNote(
                                        item as DeliveryNoteData,
                                        deliveryNoteBundleKey
                                    );
                                }
                                return undefined;
                            },
                            deletePermanentEndpoint: (item: TableRowData) => {
                                if (isDeliveryNoteData(item)) {
                                    return deps.actionsService.deleteDeliveryNotePermanently(
                                        item as DeliveryNoteData
                                    );
                                }
                                return undefined;
                            },

                            // Context
                            context: 'consignor',

                            // Visibility conditions
                            isEditVisible: (item: TableRowData) => {
                                // Don't allow edit if bundle status is sent or closed
                                const hasSentOrClosedStatus =
                                    hasDeliveryNoteBundleStatus(item) &&
                                    (item.deliveryNoteBundleStatus === 'sent' ||
                                        item.deliveryNoteBundleStatus ===
                                            'closed');

                                if (hasSentOrClosedStatus) return false;

                                // Don't allow edit if item has been shipped
                                const isShipped =
                                    hasDateShipped(item) &&
                                    item.dateShipped > 0;
                                if (isShipped) return false;

                                // Check other conditions
                                const isNotReadOnly =
                                    !hasReadOnly(item) || !item.read_only;

                                return isNotReadOnly;
                            },
                            isDeleteVisible: (item: TableRowData) => {
                                // Don't allow delete if bundle status is sent or closed
                                const hasSentOrClosedStatus =
                                    hasDeliveryNoteBundleStatus(item) &&
                                    (item.deliveryNoteBundleStatus === 'sent' ||
                                        item.deliveryNoteBundleStatus ===
                                            'closed');

                                if (hasSentOrClosedStatus) return false;

                                // Don't allow delete if item has been shipped
                                const isShipped =
                                    hasDateShipped(item) &&
                                    item.dateShipped > 0;
                                if (isShipped) return false;

                                // Show scissors when delivery note IS in a bundle and not read-only
                                const hasBundle =
                                    hasDeliveryNoteBundleNumber(item) &&
                                    item.deliveryNoteBundleNumber !== '';
                                const isNotReadOnly =
                                    !hasReadOnly(item) || !item.read_only;

                                return hasBundle && isNotReadOnly;
                            },
                            isDeletePermanentVisible: (item: TableRowData) => {
                                // Only show permanent delete when delivery note has NO bundle number
                                const hasNoBundle =
                                    !hasDeliveryNoteBundleNumber(item) ||
                                    item.deliveryNoteBundleNumber === '';

                                const isNotReadOnly =
                                    !hasReadOnly(item) || !item.read_only;

                                return hasNoBundle && isNotReadOnly;
                            },
                        } as TableActionsConfig,
                    }),
                    ColumnCreator.deliveryNoteNumber(),
                    ColumnCreator.orderNumber(),
                    ColumnCreator.referenceNumber({
                        orgType: 'consignor',
                        readOnly: row => {
                            const isClosed =
                                hasDeliveryNoteBundleStatus(row) &&
                                row.deliveryNoteBundleStatus === 'closed';
                            const isReadOnly =
                                hasReadOnly(row) && row.read_only;
                            return isClosed || isReadOnly;
                        },
                        organizationSiteKeyResolver: row =>
                            (row as DeliveryNoteData).consignorKey || '',
                    }),
                    ColumnCreator.discrepancies(),
                    ColumnCreator.deliveryNoteBundleNumber(),
                    ColumnCreator.originalConsignor(),
                    ColumnCreator.originalConsignorGln(),
                    ColumnCreator.consignorName(),
                    ColumnCreator.carrierName(),
                    ColumnCreator.consigneeName(),
                    ColumnCreator.dateShipped({
                        header: 'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.SENT-DATE',
                    }),
                    ColumnCreator.dateAnnounced({
                        header: 'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.ADVISED-DATE',
                    }),
                    ColumnCreator.dateReceived({
                        header: 'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.RECEIVED-DATE',
                    }),
                    ColumnCreator.dnStatus({ sortable: true }),
                ],
                features: {
                    sortable: true,
                    filterable: true,
                    paginated: true,
                    selectable: true,
                    expandable: false,
                },
            },
            filterConfig: {
                filterKeys: [
                    {
                        keyName: 'number',
                        label: 'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.DELIVERY-NOTE-NUMBER',
                        translationKey:
                            'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.DELIVERY-NOTE-NUMBER',
                    },
                    {
                        keyName: 'orderNumber',
                        label: 'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.ORDER-NO',
                        translationKey:
                            'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.ORDER-NO',
                    },
                    {
                        keyName: 'properties',
                        label: 'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.REFERENCE-NUMBER',
                        translationKey:
                            'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.REFERENCE-NUMBER',
                    },
                    {
                        keyName: 'deliveryNoteBundleNumber',
                        label: 'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.DELIVERY-NO',
                        translationKey:
                            'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.DELIVERY-NO',
                    },
                    {
                        keyName: 'originalConsignor',
                        label: 'GLOBAL.ORIGINAL_CONSIGNOR',
                        translationKey: 'GLOBAL.ORIGINAL_CONSIGNOR',
                    },
                    {
                        keyName: 'originalConsignorGln',
                        label: 'GLOBAL.GLN',
                        translationKey: 'GLOBAL.GLN',
                    },
                    {
                        keyName: 'consignorName',
                        label: 'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.CONSIGNOR-NAME',
                        translationKey:
                            'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.CONSIGNOR-NAME',
                    },
                    {
                        keyName: 'carrierName',
                        label: 'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.CARRIER-NAME',
                        translationKey:
                            'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.CARRIER-NAME',
                    },
                    {
                        keyName: 'consigneeName',
                        label: 'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.CONSIGNEE-NAME',
                        translationKey:
                            'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.CONSIGNEE-NAME',
                    },
                    {
                        keyName: 'deliveryNoteBundleStatus',
                        label: 'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.STATE',
                        translationKey:
                            'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.STATE',
                    },
                    {
                        keyName: 'checkoutDate',
                        label: 'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.SENT-DATE',
                        translationKey:
                            'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.SENT-DATE',
                    },
                    {
                        keyName: 'dateAnnounced',
                        label: 'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.ADVISED-DATE',
                        translationKey:
                            'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.ADVISED-DATE',
                    },
                    {
                        keyName: 'checkinDate',
                        label: 'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.RECEIVED-DATE',
                        translationKey:
                            'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.RECEIVED-DATE',
                    },
                    {
                        keyName: 'discrepancies',
                        label: 'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.DISCREPANCIES',
                        translationKey:
                            'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.DISCREPANCIES',
                    },
                ],
                showClosed: true,
                enableAdvancedFilters: true,
            },
            headerActionConfig: {
                headerActions: [
                    {
                        id: 'createFolder',
                        label: 'Create Folder',
                        translationKey: 'OUTGOING-GOODS.BUTTONS.CREATE-FOLDER',
                        svgIcon: 'folder-create',
                        color: 'primary',
                        variant: 'raised',
                        cssClasses:
                            'fix-button-position tw-mr-3 tw-h-14 xl:tw-px-4 xl:!tw-text-xl',
                        position: 'right',
                        order: 1,
                        visible: (item, selection) => true,
                        disabled: (item, selection) => {
                            if (!selection || selection.length === 0)
                                return true;
                            const firstOrgSiteKey = selection[0]?.orgSiteKey;
                            const areSelectedNotesOfSameSite = selection.every(
                                note => note.orgSiteKey === firstOrgSiteKey
                            );
                            return !areSelectedNotesOfSameSite;
                        },
                        handler: async (item, selection) => {
                            const currentSelection =
                                deps.selectionStore.selectedItems();
                            const selectionCount =
                                deps.selectionStore.selectionCount();
                            const context = deps.selectionStore.contextData();

                            // Filter to only delivery note data items
                            const deliveryNoteItems = (
                                currentSelection as DeliveryNoteData[]
                            ).filter(
                                dn => !dn.deliveryNoteBundleNumber && dn.number
                            );

                            console.log(
                                'deliveryNoteItems for folder creation:',
                                currentSelection.length
                            );

                            if (currentSelection.length > 0) {
                                await deps.actionsService.createFolderFromDeliveryNotes(
                                    deliveryNoteItems
                                );
                            } else {
                            }
                        },
                        tooltip: 'OUTGOING-GOODS.BUTTONS.CREATE-FOLDER',
                    },
                    {
                        id: 'uploadDeliveryNotes',
                        label: 'Upload Delivery Notes',
                        translationKey:
                            'OUTGOING-GOODS.BUTTONS.UPLOAD-DELIVERYNOTES',
                        icon: 'cloud_upload',
                        color: 'primary',
                        variant: 'raised',
                        cssClasses:
                            'fix-button-position tw-h-14 xl:!tw-text-xl',
                        position: 'right',
                        order: 2,
                        visible: (item, selection) => true,
                        disabled: (item, selection) => false,
                        handler: async (item, selection) => {
                            const context = deps.selectionStore.contextData();

                            await deps.actionsService.uploadDeliveryNotes();
                        },
                        tooltip: 'OUTGOING-GOODS.BUTTONS.UPLOAD-DELIVERYNOTES',
                    },
                ],
            },
            searchConfig: {
                validSortFields: [
                    'dateCreated',
                    'number',
                    'orderNumber',
                    'consignorName',
                    'consigneeName',
                    'carrierName',
                    'deliveryNoteBundleNumber',
                    'dateShipped',
                    'dateAnnounced',
                    'dateReceived',
                    'status',
                ],
                defaultSortField: 'dateCreated',
                defaultSortDirection: 'desc',
            },
            permissions: {
                canCreate: true,
                canUpdate: true,
                canDelete: true,
                canExport: true,
                canImport: false,
            },
            selectionConfig: {
                clearOnViewTypeSwitch: true,
            },
        };
    }
}
